import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Radar, LogIn, ShieldCheck, KeySquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (status === "authed") {
      const dest = location.state?.from || "/globe";
      navigate(dest, { replace: true });
    }
  }, [status, navigate, location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/globe", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen speculo-grid-bg flex items-center justify-center px-4 py-10"
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-sm border border-cyan-400/60 flex items-center justify-center speculo-glow">
            <Radar className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold tracking-[0.28em] text-cyan-200">SPECULO</div>
            <div className="text-[10px] text-cyan-500/70 tracking-[0.4em] uppercase font-mono">
              Global Insight
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="speculo-glass rounded-sm p-8"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 mb-1 text-cyan-300">
            <ShieldCheck className="w-4 h-4" />
            <h1 className="text-sm uppercase tracking-widest font-semibold">Operator Access</h1>
          </div>
          <p className="text-xs text-slate-400 mb-6">
            Authenticate to enter the telemetry command deck.
          </p>

          <label className="block text-[11px] uppercase tracking-widest text-cyan-500/80 mb-1">
            Email
          </label>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 bg-[#04121a] border border-cyan-900/60 focus:border-cyan-400/70 rounded-sm px-3 py-2 font-mono text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-all"
            data-testid="login-email-input"
            placeholder="admin@speculo.io"
          />

          <label className="block text-[11px] uppercase tracking-widest text-cyan-500/80 mb-1">
            Passphrase
          </label>
          <div className="relative mb-6">
            <KeySquare className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cyan-500/60" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#04121a] border border-cyan-900/60 focus:border-cyan-400/70 rounded-sm pl-9 pr-3 py-2 font-mono text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-all"
              data-testid="login-password-input"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              className="mb-4 text-xs text-[#ff4d70] border border-[#FF003C]/40 bg-[#FF003C]/10 rounded-sm px-3 py-2 font-mono"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-3 bg-cyan-400 text-[#040d14] font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-cyan-300 hover:shadow-[0_0_18px_rgba(0,229,255,0.5)] transition-all disabled:opacity-50 disabled:cursor-wait"
            data-testid="login-submit-button"
          >
            <LogIn className="w-4 h-4" />
            {submitting ? "Establishing Link…" : "Engage"}
          </button>

          <div className="mt-6 flex items-center justify-between text-[11px] text-slate-400">
            <span className="uppercase tracking-widest">No credentials?</span>
            <Link
              to="/register"
              className="text-cyan-300 hover:text-cyan-200 uppercase tracking-widest font-semibold"
              data-testid="login-goto-register"
            >
              Request access →
            </Link>
          </div>
        </form>

        <div className="mt-6 text-center text-[10px] text-cyan-500/50 uppercase tracking-[0.4em] font-mono">
          Encrypted // JWT // HS256
        </div>
      </div>
    </div>
  );
}
